import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { analyticsApi } from '@/api';
import {
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatTile,
} from '@/components/ui';
import { ExportMenu } from '@/features/analytics/ExportMenu';
import { PeriodPicker } from '@/features/analytics/PeriodPicker';
import { ReportTable } from '@/features/analytics/ReportTable';
import { TrendChart } from '@/features/analytics/TrendChart';
import { count, days, percent } from '@/features/analytics/format';
import { VERIFICATION_SERIES } from '@/features/analytics/series';
import { errorMessage } from '@/hooks/useApiError';

const STATUS_LABELS = {
  UNVERIFIED: 'Not decided',
  PENDING: 'Waiting',
  VERIFIED: 'Verified',
  REJECTED: 'Rejected',
};

function Standing({ title, description, counts }) {
  const rows = Object.entries(counts ?? {});
  const total = rows.reduce((sum, [, value]) => sum + value, 0);

  return (
    <Card>
      <CardHeader title={title} description={description} />
      {rows.length === 0 ? (
        <EmptyState compact title="Nothing to count yet" />
      ) : (
        <dl className="space-y-2 text-sm">
          {rows.map(([status, value]) => (
            <div key={status} className="flex items-baseline justify-between gap-3">
              <dt className="text-ink-muted">{STATUS_LABELS[status] ?? status}</dt>
              <dd className="text-ink font-medium tabular-nums">
                {count(value)}
                <span className="text-ink-muted ml-2 text-xs">
                  {percent(total ? Math.round((value * 1000) / total) / 10 : null)}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
}

const EXPORT_SHEETS = [
  {
    key: 'verification-summary',
    label: 'Verification summary',
    hint: 'Outcomes, backlog and time to decide',
  },
  {
    key: 'verification-trend',
    label: 'Requests and decisions',
    hint: 'Per bucket, over the period',
  },
  {
    key: 'verification-reviewers',
    label: 'Who decided',
    hint: 'Approvals and rejections per reviewer',
  },
];

/**
 * Whether verification is keeping up, and who is deciding what.
 *
 * Approvals and rejections are never added together anywhere on this page. A
 * queue that clears itself by rejecting everything produces the same "handled"
 * total as one doing the work, and the whole point of the screen is that the
 * two look different.
 */
export function AdminVerificationReportPage() {
  const [period, setPeriod] = useState(90);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['platform-analytics', 'verification', period],
    queryFn: () => analyticsApi.getVerificationReport(period),
  });

  if (isPending) return <LoadingState label="Counting decisions…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const { outcomes, backlog } = data;
  const decided =
    outcomes.companies_approved +
    outcomes.companies_rejected +
    outcomes.students_approved +
    outcomes.students_rejected;

  return (
    <div>
      <PageHeader
        title="Verification"
        description="What was decided, what is waiting, and how long it took."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <PeriodPicker value={period} onChange={setPeriod} />
            <ExportMenu sheets={EXPORT_SHEETS} days={period} />
          </div>
        }
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Decided"
          value={count(decided)}
          hint={`${count(outcomes.company_requests)} companies asked in this period`}
        />
        <StatTile
          label="Companies approved"
          value={percent(outcomes.company_approval_rate)}
          hint={`${count(outcomes.companies_approved)} approved · ${count(
            outcomes.companies_rejected,
          )} rejected`}
        />
        <StatTile
          label="Time to decide"
          value={days(data.median_days_to_decide)}
          // Companies only: a student submits nothing, so the same interval
          // would measure the age of their account instead.
          hint="Median, from a company's request to a decision"
        />
        <StatTile
          label="Waiting now"
          value={count(backlog.companies_pending)}
          hint={
            backlog.oldest_company_request_age_days == null
              ? 'Nothing in the queue'
              : `Oldest has waited ${days(backlog.oldest_company_request_age_days)}`
          }
        />
      </div>

      <Card className="mb-6">
        <CardHeader
          title="Requests against decisions"
          description="Either line alone says whether the team was busy. Both together say whether the queue is keeping up."
        />
        <TrendChart
          points={data.trend}
          series={VERIFICATION_SERIES}
          granularity={data.period.granularity}
          emptyLabel="No requests or decisions in this period"
        />
      </Card>

      <div className="mb-6 grid gap-6 lg:grid-cols-3">
        <Standing
          title="Companies"
          description="Every company, by where its verification stands."
          counts={data.standing?.companies}
        />
        <Standing
          title="Students"
          description="Every student. Students do not submit a request, so most sit undecided."
          counts={data.standing?.students}
        />
        <Card>
          <CardHeader title="Students awaiting a decision" />
          <p className="text-ink text-3xl font-semibold tabular-nums">
            {count(backlog.students_undecided)}
          </p>
          {/* This looks like the company queue above and is not one. Saying so
              here is cheaper than having somebody chase a backlog that is
              really just the size of the student base. */}
          <p className="text-ink-muted mt-3 text-xs">
            Not a queue. A student never asks to be verified, so this is everyone nobody has ruled
            on — it grows with registration whether or not anything is wrong. The company figure
            beside it is a real backlog of requests.
          </p>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Who decided"
          description="Both columns, always. Somebody who approves everything is fast, and approvals alone would make that look like the best possible work."
        />
        <ReportTable
          rows={data.reviewers}
          rowKey={(row) => row.user_id ?? row.email ?? 'unknown'}
          empty="Nobody decided anything in this period"
          columns={[
            {
              key: 'name',
              header: 'Reviewer',
              cell: (row) => (
                <>
                  <span className="text-ink font-medium">{row.name ?? 'Deleted account'}</span>
                  {row.email && <span className="text-ink-muted block text-xs">{row.email}</span>}
                </>
              ),
            },
            {
              key: 'approved',
              header: 'Approved',
              numeric: true,
              cell: (row) => count(row.approved),
            },
            {
              key: 'rejected',
              header: 'Rejected',
              numeric: true,
              cell: (row) => count(row.rejected),
            },
            {
              key: 'approval_rate',
              header: 'Approval rate',
              numeric: true,
              cell: (row) => percent(row.approval_rate),
            },
          ]}
        />
      </Card>
    </div>
  );
}
